import React, { useEffect, useState, useRef, lazy, Suspense, memo } from "react";
import { 
  LogIn, 
  ArrowRight, 
  Check,
  Sparkles, 
  Scan, 
  LayoutGrid, 
  ShieldCheck, 
  Activity, 
  TrendingDown,
  Globe,
  Terminal,
  MapPin,
  Smartphone,
  Zap,
  Split,
  Database,
  MessageSquare,
  Search,
  ShoppingCart,
  ShieldAlert,
  BarChart3,
  Star,
  Navigation as NavigationIcon,
  Crown,
  Layers,
  Trophy,
  Minus,
  Radar,
  Download,
  Cpu,
  User as UserIcon,
  Send as SendIcon,
  Loader2,
  ChevronDown,
  Network,
  Fingerprint,
  Dna,
  Lock
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring, animate, useInView } from "framer-motion";
import { cn } from "../src/lib/utils";
import { publicSupabaseClient } from "../src/lib/publicSupabaseClient";
import 'leaflet/dist/leaflet.css';
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "./common/LanguageSelector";
import Magnetic from "./common/Magnetic";
import SEO from "./common/SEO";
import { LANGUAGES, CURRENCIES } from "../constants";
import { Navbar } from "./common/Navbar";
import { Footer } from "./Footer";
import DirectoryListingsSection from "./DirectoryListingsSection";
import asSeenPublications from "../src/data/asSeenPublications.json";
import { featurePath, LANDING_FEATURE_SLUGS, LANDING_STEP_SLUGS } from "../src/content/featurePages";

const PricingPage = lazy(() => import("./PricingTable"));
const AssistantChatDemo = lazy(() => import("./AssistantChatDemo"));
const ScanDemo = lazy(() => import("./HowItWorksDemos").then(m => ({ default: m.ScanDemo })));
const CompareDemo = lazy(() => import("./HowItWorksDemos").then(m => ({ default: m.CompareDemo })));
const TrackDemo = lazy(() => import("./HowItWorksDemos").then(m => ({ default: m.TrackDemo })));
const HeroAssistantDemo = lazy(() => import("./HeroAssistantDemo"));

// Public supabase client for real-time observation (shared — see src/lib/publicSupabaseClient.ts)
const supabase = publicSupabaseClient;


// ─── Design Tokens & Global Styles ───────────────────────────────────────────

const RedditIcon = ({ size = 24, className = "" }: { size?: number, className?: string }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 512 512" 
    xmlns="http://www.w3.org/2000/svg" 
    fillRule="evenodd" 
    clipRule="evenodd" 
    strokeLinejoin="round" 
    strokeMiterlimit="2"
    className={className}
  >
    <circle cx="-892" cy="179" r="262" fill="currentColor" transform="matrix(.9771 0 0 .9771 1127.57 81.1)"/>
    <g transform="translate(1.249 1.608) scale(1.99024)">
      <circle cx="200.6" cy="123.7" r="29.9" fill="url(#prefix___Radial1)"/>
      <circle cx="55.4" cy="123.7" r="29.9" fill="url(#prefix___Radial2)"/>
      <ellipse cx="128.1" cy="149.3" rx="85.3" ry="64" fill="url(#prefix___Radial3)"/>
      <path d="M102.8 143.1c-.5 10.8-7.7 14.8-16.1 14.8-8.4 0-14.8-5.6-14.3-16.4.5-10.8 7.7-18 16.1-18 8.4 0 14.8 8.8 14.3 19.6zM183.6 141.5c.5 10.8-5.9 16.4-14.3 16.4s-15.6-3.9-16.1-14.8c-.5-10.8 5.9-19.6 14.3-19.6s15.6 7.1 16.1 18z" fill="#1A2B3C" fillRule="nonzero"/>
      <path d="M153.3 144.1c.5 10.1 7.2 13.8 15 13.8s13.8-5.5 13.4-15.7c-.5-10.1-7.2-16.8-15-16.8s-13.9 8.5-13.4 18.7z" fill="url(#prefix___Radial4)" fillRule="nonzero"/>
      <path d="M102.8 144.1c-.5 10.1-7.2 13.8-15 13.8s-13.8-5.5-13.3-15.7c.5-10.1 7.2-16.8 15-16.8s13.8 8.5 13.3 18.7z" fill="url(#prefix___Radial5)" fillRule="nonzero"/>
      <path d="M128.1 165.1c-10.6 0-20.7.5-30.1 1.4-1.6.2-2.6 1.8-2 3.2 5.2 12.3 17.6 21 32.1 21s26.8-8.6 32.1-21c.6-1.5-.4-3.1-2-3.2-9.4-.9-19.5-1.4-30.1-1.4z" fill="#bbcfda" fillRule="nonzero"/>
      <path d="M128.1 167.5c-10.6 0-20.7.5-30 1.5-1.6.2-2.6 1.8-2 3.3 5.2 12.5 17.6 21.3 32 21.3 14.4 0 26.8-8.8 32-21.3.6-1.5-.4-3.1-2-3.3-9.4-1-19.5-1.5-30-1.5z" fill="#fff" fillRule="nonzero"/>
      <path d="M128.1 166.2c-10.4 0-20.3.5-29.5 1.4-1.6.2-2.6 1.8-2 3.2 5.2 12.3 17.3 21 31.5 21s26.3-8.6 31.5-21c.6-1.5-.4-3.1-2-3.2-9.2-.8-19.1-1.4-29.5-1.4z" fill="url(#prefix___Radial6)" fillRule="nonzero"/>
      <circle cx="174.8" cy="55.5" r="21.2" fill="url(#prefix___Radial7)"/>
      <path d="M127.8 88c-2.5 0-4.6-1.1-4.6-2.7 0-19 15.4-34.4 34.4-34.4 2.5 0 4.6 2.1 4.6 4.6 0 2.5-2.1 4.6-4.6 4.6-13.9 0-25.2 11.3-25.2 25.2 0 1.7-2.1 2.7-4.6 2.7z" fill="url(#prefix___Radial8)" fillRule="nonzero"/>
      <path d="M97.3 149.1c0 3.9-4.2 5.7-9.3 5.7-5.1 0-9.3-1.8-9.3-5.7 0-3.9 4.2-7.1 9.3-7.1 5.1 0 9.3 3.1 9.3 7.1zM177.5 149.1c0 3.9-4.2 5.7-9.3 5.7-5.1 0-9.3-1.8-9.3-5.7 0-3.9 4.2-7.1 9.3-7.1 5.1 0 9.3 3.1 9.3 7.1z" fill="#10B981" fillRule="nonzero"/>
      <ellipse cx="94.4" cy="134.8" rx="3.3" ry="3.6" fill="#ffc49c"/>
      <ellipse cx="173.3" cy="134.8" rx="3.3" ry="3.6" fill="#ffc49c"/>
    </g>
    <defs>
      <radialGradient id="prefix___Radial1" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(59.9015 0 0 -52.2545 201.012 107.557)">
        <stop offset="0" stopColor="#feffff"/><stop offset=".4" stopColor="#feffff"/><stop offset=".51" stopColor="#f9fcfc"/><stop offset=".62" stopColor="#edf3f5"/><stop offset=".7" stopColor="#dee9ec"/><stop offset=".72" stopColor="#d8e4e8"/><stop offset=".76" stopColor="#ccd8df"/><stop offset=".8" stopColor="#c8d5dd"/><stop offset=".83" stopColor="#ccd6de"/><stop offset=".85" stopColor="#d8dbe2"/><stop offset=".88" stopColor="#ede3e9"/><stop offset=".9" stopColor="#ffebef"/><stop offset="1" stopColor="#ffebef"/>
      </radialGradient>
      <radialGradient id="prefix___Radial2" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(59.9015 0 0 -52.2545 55.892 107.557)">
        <stop offset="0" stopColor="#feffff"/><stop offset=".4" stopColor="#feffff"/><stop offset=".51" stopColor="#f9fcfc"/><stop offset=".62" stopColor="#edf3f5"/><stop offset=".7" stopColor="#dee9ec"/><stop offset=".72" stopColor="#d8e4e8"/><stop offset=".76" stopColor="#ccd8df"/><stop offset=".8" stopColor="#c8d5dd"/><stop offset=".83" stopColor="#ccd6de"/><stop offset=".85" stopColor="#d8dbe2"/><stop offset=".88" stopColor="#ede3e9"/><stop offset=".9" stopColor="#ffebef"/><stop offset="1" stopColor="#ffebef"/>
      </radialGradient>
      <radialGradient id="prefix___Radial3" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(180.687 0 0 -126.865 130.347 99.176)">
        <stop offset="0" stopColor="#feffff"/><stop offset=".4" stopColor="#feffff"/><stop offset=".51" stopColor="#f9fcfc"/><stop offset=".62" stopColor="#edf3f5"/><stop offset=".7" stopColor="#dee9ec"/><stop offset=".72" stopColor="#d8e4e8"/><stop offset=".76" stopColor="#ccd8df"/><stop offset=".8" stopColor="#c8d5dd"/><stop offset=".83" stopColor="#ccd6de"/><stop offset=".85" stopColor="#d8dbe2"/><stop offset=".88" stopColor="#ede3e9"/><stop offset=".9" stopColor="#ffebef"/><stop offset="1" stopColor="#ffebef"/>
      </radialGradient>
      <radialGradient id="prefix___Radial4" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(-15.0964 0 0 22.1628 165.28 150.971)">
        <stop offset="0" stopColor="#34D399"/><stop offset=".5" stopColor="#10B981"/><stop offset=".7" stopColor="#059669"/><stop offset=".82" stopColor="#047857"/><stop offset=".92" stopColor="#065f46"/><stop offset="1" stopColor="#064e3b"/>
      </radialGradient>
      <radialGradient id="prefix___Radial5" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(15.0964 0 0 22.1628 90.19 150.971)">
        <stop offset="0" stopColor="#34D399"/><stop offset=".5" stopColor="#10B981"/><stop offset=".7" stopColor="#059669"/><stop offset=".82" stopColor="#047857"/><stop offset=".92" stopColor="#065f46"/><stop offset="1" stopColor="#064e3b"/>
      </radialGradient>
      <radialGradient id="prefix___Radial6" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(53.2322 0 0 -35.1106 128.369 194.908)">
        <stop offset="0" stopColor="#172e35"/><stop offset=".29" stopColor="#0e1c21"/><stop offset=".73" stopColor="#030708"/><stop offset="1" stopColor="#000000"/>
      </radialGradient>
      <radialGradient id="prefix___Radial7" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(46.7274 0 0 -46.7274 175.312 34.106)">
        <stop offset="0" stopColor="#feffff"/><stop offset=".4" stopColor="#feffff"/><stop offset=".51" stopColor="#f9fcfc"/><stop offset=".62" stopColor="#edf3f5"/><stop offset=".7" stopColor="#dee9ec"/><stop offset=".72" stopColor="#d8e4e8"/><stop offset=".76" stopColor="#ccd8df"/><stop offset=".8" stopColor="#c8d5dd"/><stop offset=".83" stopColor="#ccd6de"/><stop offset=".85" stopColor="#d8dbe2"/><stop offset=".88" stopColor="#ede3e9"/><stop offset=".9" stopColor="#ffebef"/><stop offset="1" stopColor="#ffebef"/>
      </radialGradient>
      <radialGradient id="prefix___Radial8" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(38.3003 0 0 -38.3003 155.84 85.046)">
        <stop offset="0" stopColor="#7a9299"/><stop offset=".48" stopColor="#7a9299"/><stop offset=".67" stopColor="#172e35"/><stop offset=".75" stopColor="#000000"/><stop offset=".82" stopColor="#172e35"/><stop offset="1" stopColor="#172e35"/>
      </radialGradient>
    </defs>
  </svg>
);

const GlobalStyles = memo(() => (
  <style>{`
    :root {
      --primary: #10B981;
      --primary-rgb: 16 185 129;
      --charcoal: #0A0F1E;
      --charcoal-rgb: 10 15 30;
      --slate: #1A2B3C;
      --ghost: #F8FAFC;
    }
    
    html { scroll-behavior: smooth; }
    body {
      background-color: var(--charcoal);
      color: var(--ghost);
      font-family: var(--font-sans);
      overflow-x: hidden;
      margin: 0;
    }

    /* Branded Emerald Scrollbar */
    ::-webkit-scrollbar { width: 8px; height: 8px; }
    ::-webkit-scrollbar-track { background: var(--charcoal); }
    ::-webkit-scrollbar-thumb { 
      background: var(--primary); 
      border-radius: 20px; 
      border: 2px solid var(--charcoal);
    }
    ::-webkit-scrollbar-thumb:hover { background: #34D399; }

    .noise-overlay {
      position: fixed; inset: 0; pointer-events: none; z-index: 9999;
      opacity: 0.08;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
      transform: translate3d(0,0,0);
      backface-visibility: hidden;
    }

    .font-outfit { font-family: "Outfit", sans-serif; }
    .font-instrument { font-family: "Instrument Serif", serif; }
    .font-mono { font-family: var(--font-mono); }
    
    .cursor-dot { width: 6px; height: 6px; background-color: var(--primary); box-shadow: 0 0 10px var(--primary); }
    .cursor-outline { width: 34px; height: 34px; border: 1px solid var(--primary); opacity: 0.5; }

    .glass-surface { background: rgba(26, 43, 60, 0.4); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.05); }

    /* Fluid Typography */
    .landing-page .h-hero { font-size: clamp(2.125rem, 5.5vw, 4.5rem); line-height: 1.08; text-wrap: balance; }
    .landing-page .h-hero-italic { font-size: clamp(2.375rem, 6.25vw, 5rem); line-height: 1.12; }
    .landing-page .h-section { font-size: clamp(1.75rem, 3.5vw, 3rem); line-height: 1.14; text-wrap: balance; }
    .landing-page .h-step { font-size: clamp(2rem, 4.4vw, 4rem); line-height: 1.12; text-wrap: balance; }
    .landing-page .p-large { font-size: clamp(1rem, 1.6vw, 1.25rem); line-height: 1.6; }

    .custom-scrollbar::-webkit-scrollbar { width: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: rgba(0,0,0,0.1); }
    .custom-scrollbar::-webkit-scrollbar-thumb { background: var(--primary); border-radius: 10px; }
    
    .matrix-node-container { perspective: 1000px; }
    
    .pricingTableCardFooter, .cl-pricingTableCardFooter { display: none !important; }
    .reveal-word { display: inline-block; }

    .will-change-transform { will-change: transform; }
  `}</style>
));

// ─── Constants ───────────────────────────────────────────────────────────────

const TESTIMONIALS = [
  { avatar: "JD", color: "#22d3a0" },
  { avatar: "JS", color: "#60a5fa" },
  { avatar: "EW", color: "#f472b6" },
  { avatar: "MG", color: "#a78bfa" },
  { avatar: "SB", color: "#fb923c" },
  { avatar: "DL", color: "#34d399" },
  { avatar: "JT", color: "#818cf8" },
  { avatar: "CE", color: "#22d3a0" },
];



const CustomCursor = () => {
  const mouseX = useMotionValue(-100);
  const mouseY = useMotionValue(-100);
  const dotX = useSpring(mouseX, { stiffness: 600, damping: 30 });
  const dotY = useSpring(mouseY, { stiffness: 600, damping: 30 });
  const outlineX = useSpring(mouseX, { stiffness: 100, damping: 20 });
  const outlineY = useSpring(mouseY, { stiffness: 100, damping: 20 });

  useEffect(() => {
    let ticking = false;
    const update = (e: MouseEvent) => { 
      if (!ticking) {
        window.requestAnimationFrame(() => {
          mouseX.set(e.clientX); 
          mouseY.set(e.clientY); 
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("mousemove", update, { passive: true });
    return () => window.removeEventListener("mousemove", update);
  }, [mouseX, mouseY]);

  return (
    <>
      <motion.div className="cursor-dot fixed top-0 left-0 rounded-full z-[10000] pointer-events-none hidden sm:block will-change-transform" style={{ x: dotX, y: dotY, translateX: "-50%", translateY: "-50%", backfaceVisibility: 'hidden' }} />
      <motion.div className="cursor-outline fixed top-0 left-0 rounded-full z-[10000] pointer-events-none hidden sm:block will-change-transform" style={{ x: outlineX, y: outlineY, translateX: "-50%", translateY: "-50%", backfaceVisibility: 'hidden' }} />
    </>
  );
};

const Badge = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => (
  <div className={cn("inline-flex items-center w-fit gap-2 px-4 py-1.5 bg-[rgb(var(--primary-rgb)/10%)] border border-[rgb(var(--primary-rgb)/20%)] rounded-full text-[var(--primary)] text-[10px] font-black uppercase tracking-wider", className)}>
    <div className="w-1.5 h-1.5 rounded-full bg-[var(--primary)] animate-pulse" />{children}
  </div>
);

// ─── Capability Matrix Node Micro-Animations ─────────────────────────────────

const NeuralSKUExtractionAnim = ({ isInView }: { isInView: boolean }) => {
  const { t, i18n } = useTranslation();
  const isMetric = !i18n.language.startsWith('en');
  const unitLabel = isMetric ? 'kg' : 'lb';
  const langObj = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];
  const currencySymbol = CURRENCIES.find(c => c.code === langObj.defaultCurrency)?.symbol || '$';

  return (
    <div className="relative w-full h-40 bg-black/40 rounded-3xl overflow-hidden border border-white/5 group">
      <div className="absolute inset-0 flex items-end justify-center gap-4 px-6 pb-6 opacity-20">
        {[1, 2, 3].map(i => (
          <div key={i} className="w-12 bg-white/10 border border-white/10 rounded-lg flex flex-col p-2 gap-2" style={{ height: i === 2 ? '70%' : '50%' }}>
            <div className="w-full h-2 bg-white/10 rounded-full" />
            <div className="w-full h-1 bg-white/5 rounded-full" />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={isInView ? { opacity: 1, scale: 1 } : {}} className="bg-[rgb(var(--charcoal-rgb)/80%)] backdrop-blur-md border border-[rgb(var(--primary-rgb)/30%)] p-4 rounded-2xl shadow-[0_0_30px_rgba(16,185,129,0.15)] flex flex-col gap-1 items-center">
          <span className="text-[10px] font-black text-[var(--primary)] uppercase tracking-tighter">{t('landing.detected')}</span>
          <div className="flex flex-col items-center">
            <span className="text-sm font-black text-white uppercase tracking-tight leading-none">{t('landing.mock_item_produce')}</span>
            <div className="flex items-baseline gap-1 mt-1"><span className="text-xl font-black text-[var(--primary)]">{currencySymbol}{isMetric ? '2.49' : '2.99'}</span><span className="text-[9px] font-bold text-white/40 uppercase">/ {unitLabel}</span></div>
          </div>
        </motion.div>
      </div>
      <motion.div animate={isInView ? { top: ["0%", "100%", "0%"] } : {}} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} className="absolute left-0 w-full h-[3px] bg-[var(--primary)] shadow-[0_0_25px_var(--primary)] z-20" />
    </div>
  );
};

const TheGlobalLatticeAnim = ({ isInView }: { isInView: boolean }) => (
  <div className="relative w-full h-40 bg-white/5 rounded-3xl border border-white/5 flex items-center justify-center overflow-hidden">
    <div className="absolute inset-0 opacity-20">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--primary)_0%,_transparent_70%)]" />
    </div>
    <div className="relative w-full h-full flex items-center justify-center">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <motion.div 
          key={i}
          animate={isInView ? { 
            opacity: [0.2, 1, 0.2],
            scale: [0.8, 1.2, 0.8]
          } : {}}
          transition={{ duration: 3, delay: i * 0.4, repeat: Infinity }}
          className="absolute w-1.5 h-1.5 rounded-full bg-[var(--primary)] shadow-[0_0_10px_var(--primary)]"
          style={{ 
            top: `${20 + Math.random() * 60}%`,
            left: `${20 + Math.random() * 60}%`
          }}
        />
      ))}
      <svg className="absolute inset-0 w-full h-full opacity-20">
        <motion.path 
          d="M 50 50 L 150 100 L 250 40" 
          stroke="var(--primary)" 
          strokeWidth="1" 
          fill="none"
          initial={{ pathLength: 0 }}
          animate={isInView ? { pathLength: 1 } : {}}
          transition={{ duration: 5, repeat: Infinity }}
        />
      </svg>
      <Globe size={48} className="text-white/10" />
    </div>
  </div>
);

const HealthRingAnim = ({ isInView }: { isInView: boolean }) => {
  const { t } = useTranslation();
  const score = 82;
  const circumference = 251;
  return (
    <div className="flex flex-col items-center justify-center h-40 bg-white/5 rounded-3xl border border-white/5">
      <div className="relative w-24 h-24">
        <svg className="w-full h-full rotate-[-90deg]"><circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="6" fill="transparent" className="text-white/5" /><motion.circle initial={false} animate={{ strokeDashoffset: isInView ? circumference * (1 - score / 100) : circumference * 0.5 }} transition={{ duration: 1.5, ease: "easeOut" }} cx="48" cy="48" r="40" stroke="var(--primary)" strokeWidth="6" fill="transparent" strokeDasharray={circumference} strokeLinecap="round" /></svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-2xl font-black text-white">{score}</span><span className="text-[7px] font-black text-[var(--primary)] uppercase text-center leading-tight px-2">{t('landing.feature_score_label')}</span></div>
      </div>
    </div>
  );
};

const RealTimeOptimizationAnim = ({ isInView }: { isInView: boolean }) => {
  const { t, i18n } = useTranslation();
  const isMetric = !i18n.language.startsWith('en');
  const langObj = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];
  const currencySymbol = CURRENCIES.find(c => c.code === langObj.defaultCurrency)?.symbol || '$';

  return (
    <div className="relative w-full h-40 bg-black/40 rounded-3xl overflow-hidden border border-white/5 p-6 flex flex-col justify-center items-center gap-4">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(16,185,129,0.05)_0%,_transparent_70%)]" />
      <div className="relative flex items-center gap-8">
        <motion.div 
          animate={isInView ? { y: [0, -5, 0], opacity: [0.4, 0.8, 0.4] } : {}}
          transition={{ duration: 3, repeat: Infinity }}
          className="flex flex-col items-center gap-1"
        >
          <div className="text-[8px] font-black text-white/20 uppercase tracking-widest">{t('landing.feature_basket_before')}</div>
          <div className="text-base font-black text-white/30 line-through tracking-tighter">{currencySymbol}{isMetric ? '132.50' : '142.50'}</div>
        </motion.div>
        
        <div className="w-px h-10 bg-white/10 relative">
          <motion.div 
            animate={isInView ? { top: ["0%", "100%", "0%"] } : {}}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-[var(--primary)] rounded-full blur-[6px] opacity-50"
          />
        </div>

        <motion.div 
          animate={isInView ? { scale: [1, 1.05, 1] } : {}}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-1"
        >
          <div className="text-[8px] font-black text-[var(--primary)] uppercase tracking-widest">{t('landing.feature_basket_after')}</div>
          <div className="text-3xl font-black text-white tracking-tighter drop-shadow-[0_0_20px_rgba(16,185,129,0.3)]">{currencySymbol}{isMetric ? '98.15' : '108.15'}</div>
        </motion.div>
      </div>
      <div className="flex gap-2 items-center">
        <div className="px-3 py-1 rounded-full bg-[rgb(var(--primary-rgb)/10%)] border border-[rgb(var(--primary-rgb)/20%)] text-[8px] font-black text-[var(--primary)] uppercase tracking-[0.2em] flex items-center gap-2">
          <div className={cn("w-1 h-1 rounded-full bg-[var(--primary)]", isInView ? "animate-pulse" : "")} />
          {t('landing.feature_basket_badge')}
        </div>
      </div>
    </div>
  );
};

const TerminalTypeAnim = ({ isInView }: { isInView: boolean }) => {
  const { t } = useTranslation();
  return (
    <div className="relative w-full h-40 bg-white/5 rounded-3xl border border-white/5 p-4 flex flex-col gap-3 overflow-hidden">
      <div className="flex flex-col gap-2 opacity-30">
        <div className="bg-white/20 p-2 rounded-xl rounded-bl-none self-start w-3/4 h-6" />
        <div className="bg-[rgb(var(--primary-rgb)/30%)] p-2 rounded-xl rounded-br-none self-end w-1/2 h-6" />
      </div>
      
      <motion.div 
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="absolute bottom-4 left-4 right-4 bg-white rounded-2xl p-3 shadow-2xl"
      >
        <div className="flex items-center gap-3 mb-3 text-left">
          <div className="p-2 bg-emerald-50 rounded-lg text-[var(--primary)] shrink-0">
            <ShieldAlert size={16} />
          </div>
          <div>
            <h4 className="text-[10px] font-black uppercase tracking-widest text-[#1a2b3c] leading-none mb-1">{t('landing.confirm_savings')}</h4>
            <p className="text-[9px] font-bold text-gray-400 leading-tight">{t('landing.switch_target')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="flex-1 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-wider text-gray-400 border border-gray-100 flex items-center justify-center">{t('common.cancel')}</button>
          <button className="flex-1 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-wider text-white bg-[var(--primary)] shadow-md shadow-[rgb(var(--primary-rgb)/20%)] flex items-center justify-center gap-1"><Check size={10}/> {t('common.save')}</button>
        </div>
      </motion.div>
    </div>
  );
};

const PriceDuelAnim = ({ isInView }: { isInView: boolean }) => {
  const { t, i18n } = useTranslation();
  const langObj = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];
  const currencySymbol = CURRENCIES.find(c => c.code === langObj.defaultCurrency)?.symbol || '$';
  
  return (
    <div className="relative w-full h-40 bg-white/5 rounded-3xl border border-white/5 flex items-center justify-center gap-4 overflow-hidden">
      <motion.div animate={isInView ? { y: [0, -15, 0] } : {}} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} className="p-4 rounded-2xl bg-[var(--slate)] border border-white/10 shadow-2xl flex flex-col items-center gap-1"><span className="text-[8px] font-black text-white/20 uppercase">{t('landing.mock_store_wfm')}</span><span className="text-xs font-black text-red-400">{currencySymbol}{t('landing.mock_price_wfm', '12.40')}</span></motion.div>
      <div className="text-xl font-black text-white/10 uppercase tracking-tighter italic">{t('landing.vs')}</div>
      <motion.div animate={isInView ? { y: [0, 15, 0] } : {}} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} className="p-4 rounded-2xl bg-[rgb(var(--primary-rgb)/10%)] border border-[rgb(var(--primary-rgb)/30%)] shadow-2xl flex flex-col items-center gap-1"><span className="text-[8px] font-black text-[rgb(var(--primary-rgb)/40%)] uppercase">{t('landing.mock_store_tgt')}</span><span className="text-xs font-black text-[var(--primary)]">{currencySymbol}{t('landing.mock_price_tgt', '10.15')}</span></motion.div>
    </div>
  );
};

// ─── Main Sections ──────────────────────────────────────────────────────────

const MatrixNode = ({ title, desc, Anim, index, to }: { title: string, desc: string, Anim: React.FC<{ isInView: boolean }>, index: number, to: string }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { margin: "-50px" });
  
  return (
    <motion.div 
      ref={ref}
      initial={{ opacity: 0, y: 60 }} 
      whileInView={{ opacity: 1, y: 0 }} 
      transition={{ duration: 0.6, delay: index * 0.1 }} 
      viewport={{ once: true }} 
      className="group p-8 sm:p-12 rounded-[2.5rem] sm:rounded-[3.5rem] bg-white/5 border border-white/10 hover:border-[rgb(var(--primary-rgb)/30%)] transition-all duration-700 flex flex-col gap-8 sm:gap-12 relative overflow-hidden will-change-transform"
    >
      <div aria-hidden="true" inert className="pointer-events-none"><Anim isInView={isInView} /></div>
      <div className="flex flex-col gap-3 sm:gap-4 text-left">
        <h3 className="font-outfit text-xl sm:text-2xl font-black text-white uppercase tracking-tight">
          <Link to={to} className="flex items-start justify-between gap-3 group-hover:text-[var(--primary)] transition-colors after:absolute after:inset-0 after:z-10 after:rounded-[inherit] focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-[-3px] focus-visible:after:outline-emerald-400">
            {title}<ArrowRight size={20} aria-hidden="true" className="mt-1 shrink-0 text-[var(--primary)] rtl:rotate-180" />
          </Link>
        </h3>
        <p className="text-white/40 font-bold text-xs sm:text-sm leading-relaxed tracking-tight">{desc}</p>
      </div>
    </motion.div>
  );
};

const AsSeenSection = memo(() => {
  const { t } = useTranslation();

  // Shared with api/ssrPublicPages.js so the SSR crawler-facing template and
  // the real client render can never drift out of sync with each other.
  const publications = asSeenPublications;

  // Repeat the array to create a seamless infinite loop (3 times is mathematically optimal for ticker translations of -33.33%)
  const tickerItems = [...publications, ...publications, ...publications];

  return (
    <section className="py-12 bg-[#050810]/60 border-y border-white/5 backdrop-blur-md relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-8 sm:px-24 w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-8 md:gap-16 relative z-10">
        
        {/* Left Side: Brand & Featured Text */}
        <div className="flex flex-col gap-2 text-left shrink-0 w-full md:w-5/12 max-w-sm">
          <span className="text-[9px] font-black text-[var(--primary)] uppercase tracking-[0.4em] select-none">
            {t('landing.as_seen_badge')}
          </span>
          <h4 className="font-outfit font-black text-white text-xl sm:text-2xl uppercase tracking-wider leading-tight select-none">
            {t('landing.as_seen_title')}
          </h4>
          <p className="text-white/40 text-xs font-bold leading-relaxed">
            {t('landing.as_seen_desc')}
          </p>
        </div>

        {/* Right Side: Rolling Ticker Loop */}
        <div className="relative flex overflow-hidden w-full md:w-7/12 md:flex-1 group/marquee py-2 select-none">
          {/* Left and Right Fade Gradients */}
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#050810] to-transparent z-20 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#050810] to-transparent z-20 pointer-events-none" />

          {/* Marquee Track */}
          <div className="flex gap-16 animate-logo-marquee whitespace-nowrap items-center shrink-0">
            {tickerItems.map((pub, i) => (
              <a
                key={i}
                href={pub.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center transition-all duration-300 transform hover:scale-110 filter hover:brightness-125 grayscale opacity-45 hover:grayscale-0 hover:opacity-100"
              >
                <img
                  src={pub.logo}
                  alt={pub.name}
                  className={`${pub.height} w-auto object-contain select-none pointer-events-none`}
                  style={{
                    filter: (pub.name === "CBS" || pub.name === "ABC" || pub.name === "AP News") ? "invert(1)" : "none"
                  }}
                />
              </a>
            ))}
          </div>
        </div>

      </div>

      <style>{`
        @keyframes logoMarquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }
        .animate-logo-marquee {
          display: flex;
          animation: logoMarquee 20s linear infinite;
        }
        .group\\/marquee:hover .animate-logo-marquee {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
});

const CapabilityMatrix = memo(() => {
  const { t } = useTranslation();
  const nodes = [
    { title: t('landing.feature_1_title'), desc: t('landing.feature_1_desc'), Anim: NeuralSKUExtractionAnim },
    { title: t('landing.feature_2_title'), desc: t('landing.feature_2_desc'), Anim: TheGlobalLatticeAnim },
    { title: t('landing.feature_3_title'), desc: t('landing.feature_3_desc'), Anim: RealTimeOptimizationAnim },
    { title: t('landing.feature_4_title'), desc: t('landing.feature_4_desc'), Anim: PriceDuelAnim },
    { title: t('landing.feature_5_title'), desc: t('landing.feature_5_desc'), Anim: HealthRingAnim },
    { title: t('landing.feature_6_title'), desc: t('landing.feature_6_desc'), Anim: TerminalTypeAnim },
  ];
  return (
    <section id="features" className="py-20 sm:py-28 px-4 sm:px-12 lg:px-24 bg-[var(--charcoal)] relative border-t border-white/5">
      <div className="max-w-6xl mx-auto flex flex-col gap-16 sm:gap-24">
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="flex flex-col gap-4 sm:gap-6 items-center lg:items-start text-center lg:text-left"><Badge>{t('landing.features_badge')}</Badge><h2 className="font-outfit h-section font-black text-white tracking-tighter leading-none uppercase">{t('landing.features_heading')}</h2><p className="text-white/60 font-bold p-large tracking-tight text-balance">{t('landing.features_intro')}</p></motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-10">
          {nodes.map((node, i) => (
            <MatrixNode key={LANDING_FEATURE_SLUGS[i] + i} {...node} index={i} to={featurePath(LANDING_FEATURE_SLUGS[i])} />
          ))}
        </div>
      </div>
    </section>
  );
});

// Each step shows a short replay of the real app (HowItWorksDemos.tsx).
// Links share their destinations with the crawler-facing homepage template.

const TheArchive = memo(() => {
  const { t } = useTranslation();
  const stages = [
    { id: "s1", title: t('landing.step1_title'), sub: t('landing.archive_stage1_sub'), desc: t('landing.archive_stage1_desc'), icon: Scan, visual: ScanDemo },
    { id: "s2", title: t('landing.step2_title'), sub: t('landing.archive_stage2_sub'), desc: t('landing.archive_stage2_desc'), icon: TrendingDown, visual: CompareDemo },
    { id: "s3", title: t('landing.step3_title'), sub: t('landing.archive_stage3_sub'), desc: t('landing.archive_stage3_desc'), icon: Activity, visual: TrackDemo }
  ];
  return (
    <section id="how-it-works" className="relative bg-[var(--charcoal)]">
      {stages.map((s, i) => (
        <div key={s.id} className="min-h-screen w-full flex items-center justify-center p-4 sm:p-12 lg:p-24 bg-[var(--charcoal)] border-t border-white/5 sticky top-0 will-change-transform" style={{ zIndex: i, transform: 'translate3d(0,0,0)', backfaceVisibility: 'hidden' }}>
          <motion.div initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: false, margin: "-20%" }} className="relative z-10 flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-40 max-w-6xl w-full py-20 lg:py-0">
            <div className="flex flex-col gap-6 sm:gap-10 flex-1 min-w-0 text-center lg:text-left items-center lg:items-start">
              <div className="flex flex-col gap-4 sm:gap-5 items-center lg:items-start">
                <div className="flex items-center gap-4 sm:gap-5"><div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl sm:rounded-3xl bg-[rgb(var(--primary-rgb)/10%)] flex items-center justify-center text-[var(--primary)] shadow-inner"><s.icon size={28} className="sm:w-9 sm:h-9" strokeWidth={2} /></div><span className="font-mono text-[11px] sm:text-[14px] font-black text-[var(--primary)] uppercase tracking-[0.5em]">{t('landing.step_prefix')} {i+1}</span></div>
                <h3 id={`landing-${s.id}-title`} className="font-outfit h-step font-black text-white tracking-tight uppercase">{s.title}</h3><p className="text-[var(--primary)] font-mono text-sm sm:text-xl uppercase tracking-widest font-black opacity-80">{s.sub}</p>
              </div>
              <p className="text-white/50 font-bold text-base sm:text-lg lg:text-xl max-w-3xl leading-relaxed tracking-tight text-balance">{s.desc}</p>
              <Link to={featurePath(LANDING_STEP_SLUGS[i])} aria-describedby={`landing-${s.id}-title`} className="inline-flex min-h-11 items-center justify-center gap-3 rounded-full border border-emerald-400/30 px-6 py-3 text-xs font-black uppercase tracking-widest text-[var(--primary)] hover:bg-emerald-400/10 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-emerald-400 transition-colors">
                {t('features_pages.common.learn_more', { defaultValue: 'Learn more' })}<ArrowRight size={16} aria-hidden="true" className="rtl:rotate-180" />
              </Link>
            </div>
            <div className="flex-1 lg:flex-none w-full lg:w-[400px] flex items-center justify-center">
              <Suspense fallback={<div className="w-full max-w-[400px] h-[640px] sm:h-[680px] rounded-[2rem] bg-white/5 animate-pulse" />}>
                <s.visual />
              </Suspense>
            </div>
          </motion.div>
        </div>
      ))}
    </section>
  );
});

// The privacy section is hidden for now (kept for later). Its nav link was
// replaced by Blog; api/ssrPublicPages.js leaves it out too.
const SHOW_PRIVACY_SECTION = false;

const SovereignProtocol = memo(() => {
  const { t } = useTranslation();
  return (
    <section id="privacy" className="py-24 sm:py-40 px-8 sm:px-24 bg-[var(--charcoal)] relative overflow-hidden border-t border-white/5">
      <div className="absolute inset-0 z-0 opacity-20"><div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-[150px]" /></div>
      <div className="relative z-10 max-w-7xl mx-auto flex flex-col gap-24 sm:gap-32 lg:flex-row items-center">
        <div className="flex flex-col gap-10 flex-1 text-center lg:text-left items-center lg:items-start">
          <Badge>{t('landing.privacy_section_badge')}</Badge>
          <h2 className="font-outfit h-section font-black text-white tracking-tighter leading-none uppercase">{t('landing.privacy_title')}</h2>
          <p className="text-white/60 font-bold p-large tracking-tight text-balance">{t('landing.privacy_desc')}</p>
          <div className="flex flex-col gap-4 w-full">
            {[
              { icon: <ShieldCheck size={20} />, title: t('landing.privacy_1_title'), desc: t('landing.privacy_1_desc') },
              { icon: <Download size={20} />, title: t('landing.privacy_2_title'), desc: t('landing.privacy_2_desc') },
              { icon: <Lock size={20} />, title: t('landing.privacy_3_title'), desc: t('landing.privacy_3_desc') }
            ].map((node, i) => (
              <div key={i} className="flex items-center gap-6 p-6 rounded-[2.5rem] bg-white/5 border border-white/5 group hover:border-blue-500/20 transition-all duration-500">
                <div className="text-blue-400 group-hover:scale-110 transition-transform">{node.icon}</div>
                <div className="flex flex-col">
                  <span className="text-white font-black uppercase text-sm tracking-tight">{node.title}</span>
                  <span className="text-white/30 text-sm font-bold tracking-wide leading-relaxed">{node.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 w-full max-w-md relative flex items-center justify-center py-20 lg:py-0">
          <div className="relative w-64 h-64 border border-white/5 rounded-full flex items-center justify-center">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 40, repeat: Infinity, ease: "linear" }} className="absolute inset-0 border border-dashed border-blue-500/20 rounded-full" />
            <div className="w-48 h-48 rounded-[3rem] bg-white/5 border border-white/10 flex items-center justify-center backdrop-blur-3xl shadow-2xl">
              <Fingerprint size={80} className="text-blue-500/40" strokeWidth={1} />
            </div>
            {/* Orbital Nodes */}
            {[0, 120, 240].map((deg, i) => (
              <motion.div 
                key={i}
                animate={{ rotate: 360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear", delay: i * -5 }}
                className="absolute inset-0"
                style={{ transform: `rotate(${deg}deg)` }}
              >
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-[var(--charcoal)] border border-white/10 flex items-center justify-center shadow-xl">
                  {i === 0 ? <Database size={16} className="text-blue-400" /> : i === 1 ? <Lock size={16} className="text-emerald-400" /> : <ShieldCheck size={16} className="text-purple-400" />}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
});

// The chat on the right is AssistantChatDemo: a replay of the real in-app chat.
const CartAssistantSection = () => {
  const { t } = useTranslation();

  const features = [
    { icon: MessageSquare, title: t('landing.assist_f1_title'), desc: t('landing.assist_f1_desc') },
    { icon: Globe, title: t('landing.assist_f2_title'), desc: t('landing.assist_f2_desc') },
    { icon: Sparkles, title: t('landing.assist_f3_title'), desc: t('landing.assist_f3_desc') },
    { icon: BarChart3, title: t('landing.assist_f4_title'), desc: t('landing.assist_f4_desc') },
  ];

  return (
    <section className="relative py-24 sm:py-40 bg-[var(--charcoal)] border-t border-white/5 overflow-hidden">
      {/* Atmosphere */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[1200px] bg-[rgb(var(--primary-rgb)/3%)] rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8 sm:px-24 w-full grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-32 items-center">
        <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="flex flex-col gap-10 text-center lg:text-left order-2 lg:order-1">
          <div className="flex flex-col gap-6 items-center lg:items-start text-center lg:text-left">
            <Badge>{t('landing.assistant_badge')}</Badge>
            <h2 className="font-outfit h-section font-black text-white tracking-tight leading-tight uppercase">{t('landing.meet_assistant')} <br /><span className="text-[var(--primary)] italic font-instrument normal-case drop-shadow-[0_0_40px_rgba(16,185,129,0.4)]">{t('chat.assistant')}.</span></h2>
            <p className="text-white/60 font-bold p-large tracking-tight max-w-xl">{t('landing.assistant_intro')}</p>
          </div>
          
          {/* Compact rows (like the privacy list): stacked on phones and desktop, two side by side on tablets */}
          <div className="flex flex-col sm:grid sm:grid-cols-2 lg:flex lg:flex-col gap-3 text-left w-full">
            {features.map((item, i) => (
              <div key={i} className="flex items-center gap-5 px-5 py-4 rounded-[2rem] bg-white/5 border border-white/5 group hover:border-[rgb(var(--primary-rgb)/20%)] transition-all duration-500">
                <div className="w-10 h-10 shrink-0 rounded-xl bg-[rgb(var(--primary-rgb)/10%)] flex items-center justify-center text-[var(--primary)] group-hover:scale-110 transition-transform"><item.icon size={18} /></div>
                <div className="flex flex-col gap-0.5 min-w-0"><span className="font-black uppercase tracking-widest text-[11px] text-white">{item.title}</span><span className="font-bold text-sm text-white/30 leading-snug">{item.desc}</span></div>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="relative order-1 lg:order-2">
          <Suspense fallback={<div className="w-full max-w-[460px] mx-auto h-[600px] sm:h-[680px] rounded-[2rem] bg-white/5 animate-pulse" />}>
            <AssistantChatDemo />
          </Suspense>
        </div>
      </div>
    </section>
  );
};

const SubjectPicker = ({ value, onChange }: { value: string, onChange: (val: string) => void }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const subjects = [
    { id: 'support', label: t('contact.subjects.support'), icon: <ShieldCheck size={14} /> },
    { id: 'feedback', label: t('contact.subjects.feedback'), icon: <MessageSquare size={14} /> },
    { id: 'partnership', label: t('contact.subjects.partnership'), icon: <Globe size={14} /> },
    { id: 'other', label: t('contact.subjects.other'), icon: <Zap size={14} /> }
  ];

  const selected = subjects.find(s => s.id === value) || subjects[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white/5 border border-white/5 rounded-2xl px-6 py-4 text-white font-bold flex items-center justify-between hover:bg-white/10 transition-all group active:scale-[0.99]"
      >
        <div className="flex items-center gap-3">
          <div className="text-emerald-500">{selected.icon}</div>
          <span className="text-sm uppercase tracking-wider">{selected.label}</span>
        </div>
        <ChevronDown size={16} className={cn("text-white/20 transition-transform duration-300", isOpen ? "rotate-180" : "")} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute top-full left-0 right-0 mt-3 z-50 bg-[#1a2b3c] border border-white/10 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl"
          >
            <div className="p-2 space-y-1">
              {subjects.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    onChange(s.id);
                    setIsOpen(false);
                  }}
                  className={cn(
                    "w-full px-4 py-3 rounded-xl flex items-center gap-3 transition-all text-left",
                    value === s.id ? "bg-emerald-500 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"
                  )}
                >
                  <div className={cn(value === s.id ? "text-white" : "text-emerald-500")}>{s.icon}</div>
                  <span className="text-[10px] font-black uppercase tracking-[0.2em]">{s.label}</span>
                  {value === s.id && <Check size={14} className="ml-auto" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ContactSection = memo(() => {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formData, setForm] = useState({ name: '', email: '', subject: 'support', message: '' });
  const sectionRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          lang: i18n.language
        })
      });

      if (response.ok) {
        setStatus('success');
        setForm({ name: '', email: '', subject: 'support', message: '' });
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error("Submission error:", err);
      setStatus('error');
    } finally {
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <section id="contact" ref={sectionRef} className="py-24 sm:py-40 px-8 sm:px-24 bg-[var(--charcoal)] relative overflow-hidden border-t border-white/5">
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-emerald-500/[0.02] rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
        <motion.div 
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="flex flex-col gap-8 text-center lg:text-left"
        >
          <div className="flex flex-col gap-6 items-center lg:items-start">
            <Badge>{t('landing.contact_badge')}</Badge>
            <h2 className="font-outfit h-section font-black text-white tracking-tighter leading-none uppercase">
              {t('contact.title')}
            </h2>
            <p className="text-white/60 font-bold p-large tracking-tight max-w-md">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="space-y-6">
            <div className="flex items-center gap-4 text-white/60">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center border border-white/10"><MessageSquare size={18} /></div>
              <span className="text-sm font-black uppercase tracking-widest">{t('contact.response_time')}</span>
            </div>
            <div className="flex items-center gap-4 text-white/60">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center border border-white/10"><Globe size={18} /></div>
              <span className="text-sm font-black uppercase tracking-widest">{t('contact.global_ops')}</span>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="bg-white/5 backdrop-blur-2xl border border-white/10 p-10 sm:p-14 rounded-[3.5rem] shadow-2xl relative"
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-white/30 uppercase tracking-[0.2em] px-2">{t('contact.name')}</label>
                <input 
                  required
                  value={formData.name}
                  onChange={e => setForm({...formData, name: e.target.value})}
                  className="w-full bg-white/5 border border-white/5 rounded-2xl px-6 py-4 text-white font-bold focus:border-emerald-500/50 outline-none transition-all" 
                  placeholder={t('landing.placeholder_name')}
                />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-white/30 uppercase tracking-[0.2em] px-2">{t('contact.email')}</label>
                <input 
                  required
                  type="email"
                  value={formData.email}
                  onChange={e => setForm({...formData, email: e.target.value})}
                  className="w-full bg-white/5 border border-white/5 rounded-2xl px-6 py-4 text-white font-bold focus:border-emerald-500/50 outline-none transition-all" 
                  placeholder={t('contact.email_placeholder')}
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-[10px] font-black text-white/30 uppercase tracking-[0.2em] px-2">{t('contact.subject')}</label>
              <SubjectPicker 
                value={formData.subject}
                onChange={val => setForm({...formData, subject: val})}
              />
            </div>

            <div className="space-y-3">
              <label className="text-[10px] font-black text-white/30 uppercase tracking-[0.2em] px-2">{t('contact.message')}</label>
              <textarea 
                required
                rows={4}
                value={formData.message}
                onChange={e => setForm({...formData, message: e.target.value})}
                className="w-full bg-white/5 border border-white/5 rounded-3xl px-6 py-4 text-white font-bold focus:border-emerald-500/50 outline-none transition-all resize-none" 
                placeholder={t('contact.message_placeholder')}
              />
            </div>

            <button 
              disabled={status === 'sending'}
              type="submit"
              className={cn(
                "w-full py-6 rounded-2xl font-black text-[11px] uppercase tracking-wider transition-all flex items-center justify-center gap-4",
                status === 'sending' ? "bg-white/10 text-white/40 cursor-not-allowed" : "bg-emerald-500 text-white hover:bg-emerald-600 shadow-xl shadow-emerald-500/20 active:scale-[0.98]"
              )}
            >
              {status === 'sending' ? <Loader2 size={18} className="animate-spin" /> : <SendIcon size={18} />}
              {status === 'sending' ? t('contact.transmitting') : t('contact.send')}
            </button>

            <AnimatePresence>
              {status === 'success' && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex items-center gap-3 text-emerald-500">
                  <Check size={18} />
                  <span className="text-[10px] font-black uppercase tracking-widest">{t('contact.success')}</span>
                </motion.div>
              )}
              {status === 'error' && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl flex items-center gap-3 text-rose-500">
                  <ShieldAlert size={18} />
                  <span className="text-[10px] font-black uppercase tracking-widest">{t('contact.error')}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </motion.div>
      </div>
    </section>
  );
});

const Testimonials = memo(() => {
  const { t } = useTranslation();
  return (
    <section id="testimonials" className="py-24 sm:py-40 bg-[var(--charcoal)] relative overflow-hidden border-t border-white/5">
      <div className="max-w-7xl mx-auto px-8 sm:px-24 mb-12 sm:mb-16 flex flex-col items-center text-center gap-8">
        <Badge>{t('landing.social_badge')}</Badge>
        <h2 className="font-outfit h-section font-black text-white tracking-tighter leading-none uppercase">{t('landing.social_title')} <br /><span className="text-[var(--primary)] italic font-instrument normal-case">{t('landing.social_span')}</span></h2>
        <motion.a 
          href="https://www.reddit.com/r/CartLens/" 
          target="_blank" 
          rel="noopener noreferrer"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="mt-8 px-10 py-5 bg-white/5 border border-white/10 rounded-[2rem] flex items-center gap-4 hover:bg-white/10 hover:border-[rgb(var(--primary-rgb)/30%)] transition-all group"
        >
          <div className="w-10 h-10 rounded-xl bg-[rgb(var(--primary-rgb)/10%)] flex items-center justify-center text-[var(--primary)] group-hover:bg-[var(--primary)] group-hover:text-white transition-colors">
            <RedditIcon size={20} />
          </div>
          <div className="flex flex-col items-start">
            <span className="text-[10px] font-black uppercase tracking-widest text-white/40">r/CartLens</span>
            <span className="text-lg font-black uppercase tracking-tight text-white">{t('landing.social_join_btn')}</span>
          </div>
          <ArrowRight size={20} className="ml-4 text-white/20 group-hover:text-[var(--primary)] transition-colors" />
        </motion.a>
      </div>

      <div className="relative flex overflow-hidden group min-h-[380px] sm:min-h-[430px]">
        <div className="flex gap-8 animate-marquee whitespace-nowrap py-10">
          {[...TESTIMONIALS, ...TESTIMONIALS].map((test, i) => (
            <div key={i} className="w-[380px] sm:w-[500px] shrink-0 p-8 sm:p-12 rounded-[3rem] bg-white/5 border border-white/5 flex flex-col gap-8 transition-all duration-500 hover:border-[rgb(var(--primary-rgb)/20%)] hover:bg-white/[0.07] group/card">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(s => <Star key={s} size={14} fill="var(--primary)" className="text-[var(--primary)]" />)}
              </div>
              <p className="text-white/80 font-bold text-lg sm:text-xl leading-relaxed whitespace-normal tracking-tight italic">"{t(`landing.test_${i % TESTIMONIALS.length}_quote`)}"</p>
              <div className="mt-auto flex items-center gap-5">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center font-black text-lg shadow-xl" style={{ backgroundColor: test.color, color: 'rgba(0,0,0,0.5)' }}>{test.avatar}</div>
                <div className="flex flex-col gap-1">
                  <span className="font-black text-white uppercase tracking-widest text-xs">{t(`landing.test_name_${i % TESTIMONIALS.length}`)}</span>
                  <span className="font-bold text-[10px] text-white/30 uppercase tracking-widest">{t(`landing.test_${i % TESTIMONIALS.length}_role`)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          display: flex;
          animation: marquee 60s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
});

// ─── Latest Intelligence (Blog Preview) ──────────────────────────────────────

interface LandingPost {
  id: string;
  title: string;
  excerpt: string;
  slug: string;
  publishedDate: string | null;
  authorName: string;
  categoryName: string;
  tags: string[];
  featuredImage: string;
  content: string;
}

const LatestIntelligence = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<LandingPost[]>([]);

  useEffect(() => {
    fetch('/api/blog/public?limit=3')
      .then(r => r.json())
      .then(d => setPosts(d.posts || []))
      .catch(() => {});
  }, []);

  if (posts.length === 0) return null;

  return (
    <section id="blog" className="py-24 sm:py-32 px-8 sm:px-24 bg-[var(--charcoal)] relative overflow-hidden border-t border-white/5">
      <div className="max-w-7xl mx-auto flex flex-col gap-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-8"
        >
          <div className="flex flex-col gap-4">
            <Badge>{t('landing.blog_badge')}</Badge>
            <h2 className="font-outfit h-section font-black text-white tracking-tighter leading-none uppercase">
              {t('landing.blog_title_1')}<br />
              <span className="italic text-[var(--primary)] font-instrument normal-case">{t('landing.blog_title_2')}</span>
            </h2>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-3 text-[11px] font-black uppercase tracking-[0.2em] text-white/40 hover:text-[var(--primary)] transition-colors shrink-0"
          >
            {t('landing.all_posts')}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((post, i) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              viewport={{ once: true }}
            >
              <Link to={`/blog/${post.slug}`} className="group flex flex-col rounded-[2rem] border border-white/5 bg-white/[0.02] overflow-hidden hover:border-white/10 hover:bg-white/[0.04] transition-all duration-500 h-full">
                {post.featuredImage ? (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={post.featuredImage}
                      alt={`${post.title} | CartLens Blog`}
                      className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-[#10B981]/10 to-[#1A2B3C]" />
                )}
                <div className="flex flex-col gap-4 p-7 flex-1">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#10B981]">
                    {post.categoryName}
                  </span>
                  <h3 className="font-outfit font-black text-lg uppercase tracking-tight leading-snug text-white group-hover:text-[var(--primary)] transition-colors">
                    {post.title}
                  </h3>
                  {post.excerpt && (
                    <p className="text-white/30 font-bold text-sm leading-relaxed line-clamp-2 flex-1">
                      {post.excerpt}
                    </p>
                  )}
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/20 flex items-center gap-2 mt-auto">
                    {t('landing.read')}
                    <svg className="group-hover:translate-x-1 transition-transform" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

export const LandingPage: React.FC<{ setShowAuthModal: (v: boolean) => void }> = ({ setShowAuthModal }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 1000], ["0%", "15%"]);
  const heroS = useTransform(scrollY, [0, 1000], [1.1, 1.3]);

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      }
    }
  }, []);

  return (
    <div ref={containerRef} className="landing-page bg-[var(--charcoal)] text-[var(--ghost)] relative font-plus-jakarta overflow-x-hidden antialiased cursor-none min-h-screen">
      <SEO page="landing" />
      <CustomCursor /><GlobalStyles /><div className="noise-overlay" />
      <Navbar onAuth={() => setShowAuthModal(true)} />
      
      {/* HERO */}
      <section className="relative w-full flex flex-col items-center px-6 sm:px-12 pt-36 sm:pt-44 lg:pt-48 pb-20 sm:pb-24 overflow-hidden bg-[var(--charcoal)]">
        <div className="absolute inset-0 z-0">
          <motion.img
            style={{ y: heroY, scale: heroS }}
            src="https://images.unsplash.com/photo-1604719312566-8912e9227c6a?q=70&w=1200&auto=format&fit=crop"
            className="absolute w-full h-full object-cover opacity-[0.12] grayscale"
            alt="CartLens - Supermarket aisle lined with shelves of packaged groceries and price-tag signage"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--charcoal)] via-[rgb(var(--charcoal-rgb)/60%)] to-transparent" />
        </div>
        <div className="relative z-10 flex flex-col items-center gap-10 max-w-6xl text-center">
          <div className="flex flex-col gap-3">
            <h1 className="font-outfit h-hero font-black text-white leading-tight tracking-tight uppercase will-change-transform">{t('landing.hero_title')} <br /><span className="font-instrument italic text-[var(--primary)] h-hero-italic normal-case tracking-tight inline-block mt-2 drop-shadow-[0_0_40px_rgba(16,185,129,0.3)]">{t('landing.hero_span')}</span></h1>
          </div>
          <p className="text-white/60 font-bold p-large max-w-3xl leading-tight text-balance">{t('landing.hero_desc')}</p>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <Magnetic><button onClick={() => setShowAuthModal(true)} className="bg-[var(--primary)] text-[var(--charcoal)] px-10 py-6 rounded-[2.5rem] font-black text-xs uppercase tracking-wider shadow-xl flex items-center gap-4 transition-all">{t('landing.cta_scan_free')} <ArrowRight size={20} /></button></Magnetic>
          </div>
        </div>
        {/* CartAssistant: a desktop window, or a phone screen on narrow columns (HeroAssistantDemo picks). */}
        <div className="relative z-10 w-full max-w-[360px] md:w-[90%] md:max-w-[1080px] mt-12 md:mt-16 lg:mt-20">
          <Suspense fallback={<div className="w-full aspect-[390/760] md:aspect-[1040/700] rounded-[3rem] md:rounded-[1.25rem] bg-white/5 animate-pulse" />}>
            <HeroAssistantDemo />
          </Suspense>
        </div>
      </section>

      <AsSeenSection />
      <TheArchive />
      <CapabilityMatrix />
      <CartAssistantSection />
      <Testimonials />
      {SHOW_PRIVACY_SECTION && <SovereignProtocol />}

      {/* PRICING */}
      <section id="pricing" className="py-20 sm:py-28 px-4 sm:px-8 md:px-16 lg:px-24 bg-[var(--charcoal)] relative overflow-hidden border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col gap-12 sm:gap-16 items-center">
          <div className="flex flex-col items-center text-center gap-6 sm:gap-8 px-2">
            <Badge>{t('landing.smart_savings_platform')}</Badge>
            <h2 className="font-outfit h-section font-black text-white tracking-tight leading-tight uppercase">
              {t('landing.simple_honest_pricing')}
            </h2>
          </div>
          <div className="w-full">
            <Suspense fallback={<div className="h-[400px] w-full bg-white/5 animate-pulse rounded-[2.5rem] sm:rounded-[3rem]" />}>
              <PricingPage onAuth={() => setShowAuthModal(true)} />
            </Suspense>
          </div>
          <Link to="/pricing#compare" className="-mt-4 sm:-mt-8 inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-[var(--primary)] hover:opacity-80 transition-opacity">
            {t('pricing_page.compare_all')} <ArrowRight size={14} />
          </Link>
          <Magnetic>
            <button 
              onClick={() => setShowAuthModal(true)} 
              className="bg-[var(--primary)] text-[var(--charcoal)] px-8 py-4 sm:px-12 sm:py-6 rounded-[2rem] sm:rounded-[3rem] font-black text-xs sm:text-sm uppercase tracking-wider shadow-2xl flex items-center gap-4 group transition-all"
            >
              {t('landing.cta_scan_free')} <ArrowRight size={20} className="sm:w-[22px] sm:h-[22px]" />
            </button>
          </Magnetic>
        </div>
      </section>

      <LatestIntelligence />
      <ContactSection />
      <DirectoryListingsSection slug="__all__" pageType="landing" />
      <Footer />
    </div>
  );
};

export default LandingPage;
